import { Component, ViewEncapsulation, Input } from '@angular/core';
import * as _ from 'lodash';
import { GlobalsService } from '../../../shared/services/globals.service';
import { BaseGridUIComponent } from '../../../shared/base-components/base-grid-ui.component';
import { StatusHistoryBus } from '../../../shared/models/StatusHistoryBus';
import { InvoiceService } from '../../../shared/services/invoice.service';
import { BaseMessageInfo } from '../../../shared/models/BaseMessageInfo';
import { CommonValues } from '../../../shared/static/CommonValues';
import { FeatureFlagService } from '../../../shared/services/feature-flag.service';
import { UseFeatureFlagSet, IFeatureFlagSet } from '../../../shared/decorators/featureflags/featureflagset-decorator';

interface ConsolidatedRow {
    // Original fields from v_InvoiceLineHistory
    invoiceLineHistoryId: any;
    invoiceLineId: any;
    invoiceId: any;
    inventoryNumber: string;
    invoiceLineDesc: string;
    fullDescription: string;
    changeCodeDesc: string;
    resource: string;
    warehouseName: string;
    quantity: number | null;
    cost: number | null;
    totalCost: number | null;
    tax: number | null;
    markupCost: number | null;
    markup: number | null;
    sortOrder: number | null;
    price: number | null;
    amount: number | null;
    isPrintable: boolean;
    isTaxable: boolean;
    updatedBy: string;
    insertDt: Date;
    avalaraTaxTypeId: string;
    // Consolidation metadata
    isBaseline: boolean;
    changedFields: Set<string>;
}

@Component({
    selector: 'invoice-lineitems-history',
    templateUrl: './invoice-lineitems-history.component.html',
    styleUrls: ['./invoice-lineitems-history.component.scss'],
    encapsulation: ViewEncapsulation.None
})
@UseFeatureFlagSet()
export class InvoiceLineItemsHistoryComponent extends BaseGridUIComponent<StatusHistoryBus, null, InvoiceService> {

    constructor(globals: GlobalsService, featureFlagService: FeatureFlagService, svc_data: InvoiceService) {
        super(globals);
        this.svc_data = svc_data;
        this.allData = this.allData.bind(this);
    }

    compFeatureFlagSet: IFeatureFlagSet = this as any;
    _active = false;
    consolidatedRows: ConsolidatedRow[] = [];
    private lineAvalaraTaxTypeId: string = null;
    private parentIsAvalara: boolean = false;
    private parentTaxTypeCode: number = 0;

    @Input() showInvoiceLineCost: boolean = true;

    public show(invoiceId, invoiceLineId, avalaraTaxTypeId: string = null, isAvalara: boolean = false, taxTypeCode: number = 0) {
        this.gridData = [];
        this.consolidatedRows = [];
        this.lineAvalaraTaxTypeId = avalaraTaxTypeId ? String(avalaraTaxTypeId).toLowerCase() : null;
        this.parentIsAvalara = !!isAvalara;
        this.parentTaxTypeCode = taxTypeCode || 0;
        this.dataLoading = true;
        this._active = true;
        this.svc_data.getLineItemHistory(invoiceId, invoiceLineId).subscribe((retData: BaseMessageInfo) => {
            if (retData.saveError) {
            } else {
                var ar = retData.primaryReturn;
                let i = 0;
                while (i < ar.length) {
                    ar[i].insertDt = new Date(ar[i].insertDt);
                    i++;
                }
                this.consolidatedRows = this.buildConsolidatedRows(ar);
            }
            this.dataLoading = false;
        });
    }

    statusNames: string[];

    public close() {
        this._active = false;
    }

    public processGridData() {
    }

    // Show both columns when any history row has that Avalara tax type.
    public historyHasUseTax(): boolean {
        if (this.compFeatureFlagSet.featureFlagSet['ui-adding-tax-type-to-invoice-lines']) {
            return this.consolidatedRows.some(e => this.isUseTaxRow(e));
        }
        return this.parentTaxTypeCode === 2;
    }

    public historyHasSalesTax(): boolean {
        if (this.compFeatureFlagSet.featureFlagSet['ui-adding-tax-type-to-invoice-lines']) {
            return this.consolidatedRows.some(e => this.isSalesTaxRow(e));
        }
        return this.parentTaxTypeCode === 1 && this.parentIsAvalara;
    }

    public historyHasGenericTax(): boolean {
        if (this.compFeatureFlagSet.featureFlagSet['ui-adding-tax-type-to-invoice-lines']) {
            return !this.parentIsAvalara;
        }
        return false;
    }

    public isUseTaxRow(row: ConsolidatedRow): boolean {
        return this.normalizeTaxTypeId(row?.avalaraTaxTypeId) === CommonValues.AvalaraTaxType_UseTax;
    }

    public isSalesTaxRow(row: ConsolidatedRow): boolean {
        return this.normalizeTaxTypeId(row?.avalaraTaxTypeId) === CommonValues.AvalaraTaxType_SalesTax;
    }

    /** Tax amount only in the column matching this history row's AvalaraTaxTypeId. */
    public taxForUseColumn(row: ConsolidatedRow): number | null {
        if (!this.compFeatureFlagSet.featureFlagSet['ui-adding-tax-type-to-invoice-lines']) {
            return row?.tax != null ? Number(row.tax) : null;
        }
        return this.isUseTaxRow(row) && row?.tax != null ? Number(row.tax) : null;
    }

    public taxForSalesColumn(row: ConsolidatedRow): number | null {
        if (!this.compFeatureFlagSet.featureFlagSet['ui-adding-tax-type-to-invoice-lines']) {
            return row?.tax != null ? Number(row.tax) : null;
        }
        return this.isSalesTaxRow(row) && row?.tax != null ? Number(row.tax) : null;
    }

    public isCellChanged(row: ConsolidatedRow, field: string): boolean {
        return !row.isBaseline && row.changedFields && row.changedFields.has(field);
    }

    public rowCallback = (context: { dataItem: ConsolidatedRow }) => {
        return { 'change-row': !context.dataItem.isBaseline };
    }

    private buildConsolidatedRows(entries: any[]): ConsolidatedRow[] {
        if (!entries || entries.length === 0) return [];

        // Sort chronologically for comparison
        entries.sort((a: any, b: any) => new Date(a.insertDt).getTime() - new Date(b.insertDt).getTime());

        const result: ConsolidatedRow[] = [];

        // First entry is the baseline
        result.push(this.toConsolidatedRow(entries[0], true, new Set<string>()));

        for (let i = 1; i < entries.length; i++) {
            const prev = entries[i - 1];
            const curr = entries[i];
            const changedFields = this.detectChanges(prev, curr);

            if (changedFields.size > 0) {
                result.push(this.toConsolidatedRow(curr, false, changedFields));
            }
        }

        // Newest on top
        return result.reverse();
    }

    private toConsolidatedRow(entry: any, isBaseline: boolean, changedFields: Set<string>): ConsolidatedRow {
        const rowTaxType = this.normalizeTaxTypeId(entry.avalaraTaxTypeId) || this.lineAvalaraTaxTypeId;
        return {
            invoiceLineHistoryId: entry.invoiceLineHistoryId,
            invoiceLineId: entry.invoiceLineId,
            invoiceId: entry.invoiceId,
            inventoryNumber: entry.inventoryNumber || '',
            invoiceLineDesc: entry.invoiceLineDesc || '',
            fullDescription: entry.fullDescription || '',
            changeCodeDesc: entry.changeCodeDesc || '',
            resource: entry.resource || '',
            warehouseName: entry.warehouseName || '',
            quantity: entry.quantity != null ? Number(entry.quantity) : null,
            cost: entry.cost != null ? Number(entry.cost) : null,
            totalCost: entry.totalCost != null ? Number(entry.totalCost) : null,
            tax: entry.tax != null ? Number(entry.tax) : null,
            markupCost: entry.markupCost != null ? Number(entry.markupCost) : null,
            markup: entry.markup != null ? Number(entry.markup) : null,
            sortOrder: entry.sortOrder != null ? Number(entry.sortOrder) : null,
            price: entry.price != null ? Number(entry.price) : null,
            amount: entry.amount != null ? Number(entry.amount) : null,
            isPrintable: !!entry.isPrintable,
            isTaxable: !!entry.isTaxable,
            updatedBy: entry.updatedBy || '',
            insertDt: entry.insertDt,
            // Prefer per-history-row AvalaraTaxTypeId from API; fall back to current line only if missing.
            avalaraTaxTypeId: rowTaxType,
            isBaseline: isBaseline,
            changedFields: changedFields
        };
    }

    private detectChanges(prev: any, curr: any): Set<string> {
        const changedFields = new Set<string>();
        const fieldsToCompare = [
            'inventoryNumber', 'invoiceLineDesc', 'fullDescription', 'changeCodeDesc',
            'resource', 'warehouseName', 'quantity', 'cost', 'totalCost', 'tax',
            'markupCost', 'markup', 'sortOrder', 'price', 'amount', 'isPrintable', 'isTaxable',
            'avalaraTaxTypeId'
        ];

        for (const field of fieldsToCompare) {
            const prevVal = field === 'avalaraTaxTypeId'
                ? this.normalizeTaxTypeId(prev[field])
                : this.normalizeValue(prev[field]);
            const currVal = field === 'avalaraTaxTypeId'
                ? this.normalizeTaxTypeId(curr[field])
                : this.normalizeValue(curr[field]);
            if (prevVal !== currVal) {
                changedFields.add(field);
            }
        }

        return changedFields;
    }

    private normalizeValue(val: any): string {
        if (val === null || val === undefined) return '__null__';
        if (typeof val === 'boolean') return val ? 'true' : 'false';
        if (typeof val === 'number') return val.toString();
        return val.toString().trim();
    }

    private normalizeTaxTypeId(val: any): string {
        if (val === null || val === undefined || val === '') {
            return null;
        }
        return String(val).toLowerCase();
    }
}
